import React, { useState, useEffect } from "react";
import {
  Box,
  Autocomplete,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import { useNavigate } from "react-router-dom";

interface Profile {
  username: string;
  type: string; // BA, account, Brand
  table_id: number;
  additional_details: string; // Assuming this is the JSON string
}

interface SearchBarProfileProps {
  onSelect?: (profile: Profile | null) => void; // Callback for selection
}

const SearchBarProfile: React.FC<SearchBarProfileProps> = ({ onSelect }) => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfiles = async () => {
      setLoading(true);
      try {
        const response = await axios.get<Profile[]>(
          `${import.meta.env.VITE_API_URL}/profiles/profile_profiles`
        );
        setProfiles(response.data);
      } catch (error) {
        console.error("Error fetching profiles:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, []); // Fetch profiles on component mount

  const handleProfileSelect = (newValue: Profile | null) => {
    if (newValue) {
      // Navigate to the profile's page using the username
      navigate(`/${newValue.username}`);
      console.log(newValue.username);
      // Call the onSelect callback if provided
      if (onSelect) {
        onSelect(newValue);
      }
    }
  };

  return (
    <Box>
      <Autocomplete
        onChange={(_, newValue) => handleProfileSelect(newValue)}
        options={profiles}
        getOptionLabel={(option) => option.username}
        renderInput={(params) => (
          <TextField
            {...params}
            onFocus={(e) => (e.target.placeholder = "Search for a profile...")}
            onBlur={(e) => (e.target.placeholder = "")}
            sx={{
              transition: "width 0.3s",
              "&:focus-within": {
                width: "23ch", // Expand width by 10% on focus (12ch + 10%)
              },
              width: "3ch", // Initial width
              "& .MuiInputBase-input": {
                color: "white", // Set text color to white
              },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "white" }} />
                </InputAdornment>
              ),
            }}
          />
        )}
        loading={loading}
        loadingText="Loading profiles..."
        noOptionsText="No profiles found"
        isOptionEqualToValue={(option, value) =>
          option.username === value.username
        }
        renderOption={(props, option) => {
          const typeLabel = option.type === "BA" ? "Ambassador" : option.type;
          return (
            <li {...props} key={option.username}>
              <Typography variant="body1">{option.username}</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                <span style={{ marginLeft: "10px" }}>{typeLabel}</span>
              </Typography>
            </li>
          );
        }}
      />
    </Box>
  );
};

export default SearchBarProfile;
